import { readableBatchId } from "@client-shared/lib/util";
import * as api from "@client-shared/services/api/batches";
import type {
  CarrierCustomerAccountBinding,
  LabelPrintBatch,
  LabelPrintBatchCreateRequest,
  LabelPrintBatchDetail,
  LabelPrintBatchFileType,
  LabelPrintBatchSummaryRequest,
  PurchaseOrder,
  PurchaseOrderStatus,
} from "@shared/types";
import { isAxiosError } from "axios";
import { setLoadingKey } from "./batches-context.actions";
import type { BatchesDispatch, BatchesInitializeOptions } from "./batches-context.types";

export const BCC_BATCH_POLLING_INTERVAL_MS = 15000;
export const BCC_BATCH_FETCH_RETRY_LIMIT = 3;
export const BCC_BATCH_DEFAULT_LIMIT = 2000;
export const BCC_BATCH_DEFAULT_PAGE_SIZE = 25;

const ERROR_PURCHASE_ORDER_STATUSES: ReadonlySet<PurchaseOrderStatus> = new Set([
  "AddressValidationError",
  "Canceled",
  "DeliveryError",
  "Error",
  "LabelError",
  "ProductError",
  "ZenkraftAddressError",
]);

const sleep = async (ms: number) => {
  await new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
};

export const isLabelPrintBatchProcessing = (batch?: Pick<LabelPrintBatch, "batchStatus" | "batchPrintStatus">): boolean => {
  return Boolean(
    batch && (batch.batchStatus === "Processing" || batch.batchStatus === "Failed-for-Retry" || batch.batchPrintStatus === "Processing")
  );
};

export const getLabelPrintBatchSearchValue = (batch: LabelPrintBatch): string => {
  return [
    readableBatchId(batch.batchId),
    batch.batchId,
    batch.batchNotes ?? "",
    batch.batchStatus,
    batch.batchPrintStatus ?? "",
    batch.totalOrders.toString(),
    batch.totalUnits.toString(),
    batch.totalWeightInGrams?.toString() ?? "",
  ]
    .join(" ")
    .toLowerCase();
};

export const canRetryLabelPrintBatch = (batch?: Pick<LabelPrintBatch, "batchStatus" | "failedForRetryOrders">): boolean => {
  if (!batch) return false;
  return Boolean(batch.failedForRetryOrders > 0 && ["Failed", "Failed-for-Retry", "PartiallyFailed"].includes(batch.batchStatus));
};

export const hasDocumentEligibleOrders = (purchaseOrders?: PurchaseOrder[]): boolean => {
  if (!purchaseOrders?.length) return false;
  return purchaseOrders.some(({ orderStatus }) => {
    if (!orderStatus) return true;
    return !ERROR_PURCHASE_ORDER_STATUSES.has(orderStatus);
  });
};

export const isBatchDocumentsActionEnabled = (batch?: LabelPrintBatchDetail): boolean => {
  if (!batch) return false;
  return Boolean(
    hasDocumentEligibleOrders(batch.purchaseOrders) &&
    !isLabelPrintBatchProcessing(batch) &&
    ["Completed", "PartiallyFailed"].includes(batch.batchStatus)
  );
};

export const hasManifestEnabledCarrier = (
  batch?: LabelPrintBatchDetail | null,
  customerAccountCarrierDetails?: CarrierCustomerAccountBinding[]
): boolean => {
  if (!batch || !customerAccountCarrierDetails?.length) return false;

  // Mirror the client's BatchManifestButton match (substring `.includes()`) so
  // the button visibility matches whenever the client would show it. The earlier
  // strict comma-split + exact-match was missing vendors whose binding strings
  // had unexpected whitespace, prefixes, or concatenated account segments.
  const customerAccountNumbers = batch.purchaseOrders
    .map((purchaseOrder) => purchaseOrder.customerAccountNumber)
    .filter((accountNumber): accountNumber is string => Boolean(accountNumber));

  const matchingDetail = customerAccountCarrierDetails.find((detail) =>
    customerAccountNumbers.some((accountNumber) => detail.customerAccountNumber.includes(accountNumber))
  );

  return Boolean(matchingDetail?.isManifestEnabled);
};

export const fetchBatchesHandler = async ({
  dispatch,
  params,
  vendorKey,
}: {
  dispatch: BatchesDispatch;
  params?: NonNullable<BatchesInitializeOptions["params"]>;
  vendorKey: string;
}): Promise<LabelPrintBatch[]> => {
  if (!vendorKey) return [];

  dispatch(setLoadingKey("fetchBatches", true));
  dispatch({ type: "SET_LOADING", payload: true });
  dispatch({ type: "SET_ERROR", payload: undefined });

  try {
    const max = params?.max ?? BCC_BATCH_DEFAULT_LIMIT;
    const resolvedParams: LabelPrintBatchSummaryRequest = {
      ...params,
      vendorKey,
      limit: params?.limit ?? BCC_BATCH_DEFAULT_LIMIT,
    };

    const batches: LabelPrintBatch[] = [];
    let nextParams: LabelPrintBatchSummaryRequest = resolvedParams;

    while (true) {
      const response = await api.getLabelPrintBatches(nextParams);
      const nextBatches = response.data.batchSummary ?? [];
      batches.push(...nextBatches);

      if (!response.data.lastBatchId || nextBatches.length < nextParams.limit) {
        break;
      }

      if (batches.length >= max) {
        break;
      }

      nextParams = {
        ...nextParams,
        lastBatchId: response.data.lastBatchId,
      };
    }

    const capped = batches.length > max ? batches.slice(0, max) : batches;
    dispatch({ type: "SET_BATCHES", payload: capped });
    dispatch({ type: "SET_SUCCESS", payload: true });
    return capped;
  } catch (error) {
    const nextError = error instanceof Error ? error : new Error("Unable to fetch batches");
    dispatch({ type: "SET_ERROR", payload: nextError });
    throw nextError;
  } finally {
    dispatch({ type: "SET_LOADING", payload: false });
    dispatch(setLoadingKey("fetchBatches", false));
  }
};

export const fetchBatchHandler = async ({
  batchId,
  dispatch,
  retryCount = 0,
}: {
  batchId: string;
  dispatch: BatchesDispatch;
  retryCount?: number;
}): Promise<LabelPrintBatchDetail | undefined> => {
  if (retryCount === 0) {
    dispatch(setLoadingKey("fetchBatch", true));
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: undefined });
  }

  try {
    const response = await api.getLabelPrintBatchById(batchId);
    const batch = response.data;
    dispatch({ type: "SET_BATCH", payload: batch });
    dispatch({ type: "SET_BATCH_ID", payload: batchId });
    dispatch({
      type: "SET_PROCESSING",
      payload: isLabelPrintBatchProcessing(batch),
    });
    dispatch({ type: "SET_SUCCESS", payload: true });
    dispatch({ type: "SET_LOADING", payload: false });
    dispatch(setLoadingKey("fetchBatch", false));
    return batch;
  } catch (error) {
    const isRetryable = isAxiosError(error) && error.response?.status === 404;

    if (isRetryable && retryCount < BCC_BATCH_FETCH_RETRY_LIMIT) {
      await sleep(Math.min(8000, 1000 * 2 ** retryCount));
      return fetchBatchHandler({
        batchId,
        dispatch,
        retryCount: retryCount + 1,
      });
    }

    const nextError = error instanceof Error ? error : new Error("Unable to fetch batch");
    dispatch({ type: "SET_ERROR", payload: nextError });
    dispatch({ type: "SET_LOADING", payload: false });
    dispatch(setLoadingKey("fetchBatch", false));
    throw nextError;
  }
};

export const createBatchHandler = async ({
  batch,
  dispatch,
}: {
  batch: LabelPrintBatchCreateRequest;
  dispatch: BatchesDispatch;
}): Promise<LabelPrintBatch | undefined> => {
  dispatch(setLoadingKey("createBatch", true));
  dispatch({ type: "SET_LOADING", payload: true });
  dispatch({ type: "SET_ERROR", payload: undefined });

  try {
    const response = await api.createLabelPrintBatch(batch);
    const createdBatch = response.data;
    if (createdBatch.batchId) {
      dispatch({ type: "SET_BATCH_ID", payload: createdBatch.batchId });
      await fetchBatchHandler({
        batchId: createdBatch.batchId,
        dispatch,
      });
    }
    return createdBatch;
  } catch (error) {
    const nextError = error instanceof Error ? error : new Error("Unable to create batch");
    dispatch({ type: "SET_ERROR", payload: nextError });
    throw nextError;
  } finally {
    dispatch({ type: "SET_LOADING", payload: false });
    dispatch(setLoadingKey("createBatch", false));
  }
};

export const getShippingDocumentLoadingKey = (batchId: string, fileType: LabelPrintBatchFileType): string =>
  `shippingDocument:${batchId}:${fileType}`;

const fetchBatchShippingDocumentUrl = async (batch: LabelPrintBatchDetail, fileType: LabelPrintBatchFileType): Promise<string | null> => {
  const response = await api.getLabelPrintBatchShippingDocument(batch.batchId, fileType);
  return typeof response.data === "string" ? response.data : null;
};

export const getLabelPrintBatchShippingDocumentUrlHandler = async ({
  batch,
  dispatch,
  fileType = "Label",
}: {
  batch: LabelPrintBatchDetail;
  dispatch: BatchesDispatch;
  fileType?: LabelPrintBatchFileType;
}): Promise<string | null> => {
  const loadingKey = getShippingDocumentLoadingKey(batch.batchId, fileType);
  dispatch(setLoadingKey(loadingKey, true));
  dispatch({ type: "SET_ERROR", payload: undefined });

  try {
    if (!isBatchDocumentsActionEnabled(batch)) {
      return null;
    }

    if (fileType === "Manifest") {
      try {
        return await fetchBatchShippingDocumentUrl(batch, fileType);
      } catch {
        await api.createLabelPrintBatchManifest(batch.batchId);
        return await fetchBatchShippingDocumentUrl(batch, fileType);
      }
    }

    return await fetchBatchShippingDocumentUrl(batch, fileType);
  } catch (error) {
    const nextError = error instanceof Error ? error : new Error("Unable to fetch shipping document");
    dispatch({ type: "SET_ERROR", payload: nextError });
    return null;
  } finally {
    dispatch(setLoadingKey(loadingKey, false));
  }
};

export const getLabelPrintBatchShippingDocumentHandler = async ({
  batch,
  dispatch,
  fileType = "Label",
}: {
  batch: LabelPrintBatchDetail;
  dispatch: BatchesDispatch;
  fileType?: LabelPrintBatchFileType;
}): Promise<string | null> => {
  return getLabelPrintBatchShippingDocumentUrlHandler({
    batch,
    dispatch,
    fileType,
  });
};
